import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import en from "@/lib/i18n/en.json";
import ar from "@/lib/i18n/ar.json";

const DICTS = { en, ar };
const I18nContext = createContext(null);

function lookup(obj, path) {
    return path.split(".").reduce((acc, k) => (acc != null ? acc[k] : undefined), obj);
}

function applyDir(lang) {
    const dir = lang === "ar" ? "rtl" : "ltr";
    document.documentElement.setAttribute("lang", lang);
    document.documentElement.setAttribute("dir", dir);
}

export function I18nProvider({ children }) {
    const [lang, setLang] = useState(() => {
        const stored = typeof window !== "undefined" ? window.localStorage.getItem("lang") : null;
        return stored === "ar" ? "ar" : "en";
    });

    useEffect(() => {
        applyDir(lang);
        try { window.localStorage.setItem("lang", lang); } catch (_) { /* ignore */ }
    }, [lang]);

    const value = useMemo(() => {
        const dict = DICTS[lang] || DICTS.en;
        const t = (key, vars) => {
            const raw = lookup(dict, key);
            if (typeof raw !== "string") return key;
            if (!vars) return raw;
            return Object.keys(vars).reduce(
                (s, k) => s.replace(new RegExp(`\\{${k}\\}`, "g"), String(vars[k])),
                raw,
            );
        };
        return {
            lang,
            dir: lang === "ar" ? "rtl" : "ltr",
            isRTL: lang === "ar",
            setLang,
            toggle: () => setLang((p) => (p === "ar" ? "en" : "ar")),
            t,
        };
    }, [lang]);

    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
    const ctx = useContext(I18nContext);
    if (!ctx) throw new Error("useI18n must be used inside I18nProvider");
    return ctx;
}
